import React from "react";
import Section from "../ui/Section/Section";
import { Grid } from "../ui/Grid/Grid";
import ChartCard from "../ui/Card/ChartCard";
import { CenterRow } from "../ui/Card/Card";
import VBars from "../charts/VBars";
import Donut from "../charts/Donut";
import Legend from "../ui/Legend/Legend";
import MiniMetric from "../ui/MiniMetric/MiniMetric";
import DeltaValue from "../ui/DeltaValue/DeltaValue";
import SectionSkeleton from "../ui/SectionSkeleton/SectionSkeleton";
import { SECTIONS } from "../../constants";
import { fmt1, fmt1Signed, fmtN } from "../../functions/format";
import { pctChange } from "../../functions/mapEffectif";
import { CHART_COLORS } from "../../styles/theme";
import { usePilotageRhStore } from "../../store/usePilotageRhStore";

const { id, title } = SECTIONS.mouvements;

const SKELETON_ROWS = [{ cols: 3, cards: [5, 5, 4] }];

/** Textes des tooltips "vs précédent" ; ajuster les suffixes ici. */
const formatPointDeltaTip = (current, previous) =>
    current !== null &&
    current !== undefined &&
    previous !== null &&
    previous !== undefined
        ? `${fmt1Signed(current - previous)} pt vs précédent`
        : undefined;

const formatPctDeltaTip = (current, previous) => {
    if (current === null || current === undefined) return undefined;
    const delta = pctChange(current, previous);
    return delta !== null ? `${fmt1Signed(delta)}% vs précédent` : undefined;
};

const MouvementsSection = ({ data, isLoading }) => {
    const compareOn = usePilotageRhStore((s) => !!s.compare[id]);

    if (isLoading && !data) {
        return (
            <Section id={id} title={title}>
                <SectionSkeleton rows={SKELETON_ROWS} />
            </Section>
        );
    }

    if (!data) return null;

    const turnoverTotal = data.turnoverVolontaire + data.turnoverInvolontaire;
    // anneau neutre quand aucune décomposition n'est disponible
    const turnoverSegments =
        turnoverTotal > 0
            ? [
                  {
                      value: data.turnoverVolontaire,
                      color: CHART_COLORS.violet,
                      tip: `Volontaire : ${fmt1(data.turnoverVolontaire)} %`,
                  },
                  {
                      value: data.turnoverInvolontaire,
                      color: CHART_COLORS.violetSoft,
                      tip: `Involontaire : ${fmt1(
                          data.turnoverInvolontaire
                      )} %`,
                  },
              ]
            : [{ value: 1, color: CHART_COLORS.line }];

    return (
        <Section id={id} title={title}>
            <Grid cols={3}>
                <ChartCard title="Entrées / Sorties">
                    <VBars
                        labels={data.labels}
                        series1={data.seriesEntrees}
                        series2={data.seriesSorties}
                        names={["Entrées", "Sorties"]}
                    />
                    <MiniMetric
                        mt="8px"
                        color={CHART_COLORS.violet}
                        label="Taux d’entrées"
                        value={
                            <DeltaValue
                                section={id}
                                tip={formatPointDeltaTip(
                                    data.tauxEntrees,
                                    data.tauxEntreesPrevious
                                )}
                            >
                                {`${fmt1(data.tauxEntrees)}% (${fmtN(
                                    data.entrees
                                )})`}
                            </DeltaValue>
                        }
                    />
                    <MiniMetric
                        color={CHART_COLORS.violetSoft}
                        label="Taux de sorties"
                        value={
                            <DeltaValue
                                section={id}
                                tip={formatPointDeltaTip(
                                    data.tauxSorties,
                                    data.tauxSortiesPrevious
                                )}
                            >
                                {`${fmt1(data.tauxSorties)}% (${fmtN(
                                    data.sorties
                                )})`}
                            </DeltaValue>
                        }
                    />
                </ChartCard>

                <ChartCard title="Décomposition du turnover" fill>
                    <CenterRow>
                        <Donut
                            segments={turnoverSegments}
                            center={`${fmt1(data.turnoverGlobal)}%`}
                            sub="GLOBAL"
                            centerTip={
                                compareOn
                                    ? formatPointDeltaTip(
                                          data.turnoverGlobal,
                                          data.turnoverGlobalPrevious
                                      )
                                    : undefined
                            }
                        />
                    </CenterRow>
                    <Legend
                        style={{ marginTop: 9 }}
                        items={[
                            {
                                name: `Volontaire ${fmt1(
                                    data.turnoverVolontaire
                                )}%`,
                                color: CHART_COLORS.violet,
                            },
                            {
                                name: `Involontaire ${fmt1(
                                    data.turnoverInvolontaire
                                )}%`,
                                color: CHART_COLORS.violetSoft,
                            },
                        ]}
                    />
                </ChartCard>

                <ChartCard title="Onboarding / Offboarding" fill>
                    <MiniMetric
                        label="Onboardings en cours"
                        value={
                            <DeltaValue
                                section={id}
                                tip={formatPctDeltaTip(
                                    data.onboardingsEnCours,
                                    data.onboardingsEnCoursPrevious
                                )}
                            >
                                {fmtN(data.onboardingsEnCours)}
                            </DeltaValue>
                        }
                    />
                    <MiniMetric
                        label="Délai moyen d’onboarding"
                        value={
                            <DeltaValue
                                section={id}
                                tip={formatPctDeltaTip(
                                    data.delaiOnboarding,
                                    data.delaiOnboardingPrevious
                                )}
                            >
                                {`${fmt1(data.delaiOnboarding)} jours`}
                            </DeltaValue>
                        }
                    />
                    <MiniMetric
                        label="Offboardings en cours"
                        value={
                            <DeltaValue
                                section={id}
                                tip={formatPctDeltaTip(
                                    data.offboardingsEnCours,
                                    data.offboardingsEnCoursPrevious
                                )}
                            >
                                {fmtN(data.offboardingsEnCours)}
                            </DeltaValue>
                        }
                    />
                    <MiniMetric
                        label="Délai moyen d’offboarding"
                        value={
                            <DeltaValue
                                section={id}
                                tip={formatPctDeltaTip(
                                    data.delaiOffboarding,
                                    data.delaiOffboardingPrevious
                                )}
                            >
                                {`${fmt1(data.delaiOffboarding)} jours`}
                            </DeltaValue>
                        }
                    />
                </ChartCard>
            </Grid>
        </Section>
    );
};

export default MouvementsSection;
